import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, ArrowRight, Zap, MessagesSquare, TrendingUp, X } from "lucide-react";

const SLIDES = [
  {
    icon: Activity,
    title: "Bem-vindo ao ConvertPix 👋",
    subtitle: "O motor que recupera vendas PIX no piloto automático.",
    bullets: [
      "Clientes que geram PIX mas não pagam entram num fluxo automático de mensagens no WhatsApp",
      "Quando pagam, o motor para. Quando não pagam, ele insiste no ritmo certo",
      "Você só precisa configurar uma vez",
    ],
  },
  {
    icon: Zap,
    title: "3 passos simples pra começar",
    subtitle: "Do zero ao primeiro disparo em menos de 5 minutos.",
    bullets: [
      "1. Conectar servidor de mensagens (infra do WhatsApp) - URL + API Key",
      "2. Escanear o QR Code do seu WhatsApp (uma vez só)",
      "3. Copiar o webhook ConvertPix pro seu checkout (Kiwify / Hotmart / etc.)",
    ],
  },
  {
    icon: TrendingUp,
    title: "Depois o motor trabalha sozinho",
    subtitle: "Você acompanha tudo em tempo real.",
    bullets: [
      "Dashboard com métricas (gerados / pagos / recuperados / taxa)",
      "Chat WhatsApp integrado: veja e responda leads em tempo real",
      "Logs de cada mensagem que o motor dispara",
    ],
  },
];

const STORAGE_KEY = "convertpix_welcome_seen_v1";

export default function WelcomeModal({ force = false, onClose }) {
  const [open, setOpen] = useState(false);
  const [slide, setSlide] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (force) {
      setOpen(true);
      return;
    }
    try {
      if (!localStorage.getItem(STORAGE_KEY)) setOpen(true);
    } catch {}
  }, [force]);

  const close = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {}
    setOpen(false);
    onClose?.();
  };

  const goSetup = () => {
    close();
    navigate("/app/connection");
  };

  if (!open) return null;
  const S = SLIDES[slide];
  const Icon = S.icon;
  const isLast = slide === SLIDES.length - 1;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="welcome-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={close} />
      <div className="cp-card relative w-full max-w-lg overflow-hidden">
        <button onClick={close} data-testid="welcome-close" className="absolute top-3 right-3 z-10 text-slate-500 hover:text-white p-1.5">
          <X className="h-4 w-4" />
        </button>

        {/* Hero illustration area */}
        <div className="relative h-40 bg-gradient-to-br from-cp-primary/20 via-cp-primary/5 to-transparent border-b border-white/[0.06] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(rgba(34,197,94,0.14)_1px,transparent_1px)] bg-[length:18px_18px]" />
          <div className="relative h-20 w-20 rounded-2xl bg-cp-primary flex items-center justify-center shadow-lg shadow-cp-primary/30">
            <Icon className="h-10 w-10 text-cp-bg" strokeWidth={2.5} />
          </div>
        </div>

        <div className="p-6 md:p-8">
          <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tighter">{S.title}</h2>
          <p className="text-slate-400 text-sm mt-2">{S.subtitle}</p>

          <ul className="mt-6 space-y-3">
            {S.bullets.map((b, i) => (
              <li key={i} className="flex gap-3 text-sm text-slate-200">
                <span className="h-5 w-5 rounded-full bg-cp-primary/15 border border-cp-primary/40 text-cp-primary-light text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <span className="leading-relaxed">{b}</span>
              </li>
            ))}
          </ul>

          {/* Progress dots */}
          <div className="flex items-center justify-center gap-2 mt-8">
            {SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => setSlide(i)}
                data-testid={`welcome-dot-${i}`}
                className={`h-1.5 rounded-full transition-all ${i === slide ? "w-8 bg-cp-primary" : "w-1.5 bg-slate-600"}`}
              />
            ))}
          </div>

          <div className="mt-6 flex items-center justify-between gap-3">
            <button
              onClick={close}
              data-testid="welcome-skip"
              className="text-sm text-slate-500 hover:text-slate-300"
            >
              Pular
            </button>
            {isLast ? (
              <button
                onClick={goSetup}
                data-testid="welcome-start"
                className="bg-cp-primary hover:bg-cp-primary-hover text-cp-bg font-bold px-5 py-2.5 rounded-md inline-flex items-center gap-2 text-sm"
              >
                Começar configuração <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                onClick={() => setSlide(slide + 1)}
                data-testid="welcome-next"
                className="bg-cp-primary hover:bg-cp-primary-hover text-cp-bg font-bold px-5 py-2.5 rounded-md inline-flex items-center gap-2 text-sm"
              >
                Próximo <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
